// @file Helper for Whiteboard (tools, colors, etc)
import device from '@@/bits/device'
import { FontNameToFontId, tailwindWhiteboardFontClass } from '@@/bits/font'
import { __ } from '@@/bits/intl'
import { ActionType, getAllShapesInGroup, isGroupShape } from '@@/bits/whiteboard'
import {
  TEXT_COLORS_TO_RGB_COLORS,
  type TldrawDefaultColorStyleColor,
  type ToolTailwindColorType,
} from '@@/bits/whiteboard_tools'
import { OzMenuRowsRowStyle, type OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import type { WhiteboardTextSize, WhiteboardTool } from '@@/types/whiteboard'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useWhiteboardHeadlessTiptapEditor } from '@@/vuecomposables/use_whiteboard_headless_tiptap_editor'
import { useWhiteboardShapeActions } from '@@/vuecomposables/use_whiteboard_shape_actions'
import type { Content } from '@padlet/whiteboard-tiptap'
import type { TLArrowShapeArrowheadStyle, TLDefaultDashStyle, TLRecord, TLShape, TLShapeId } from 'tldraw'

const WHITEBOARD_ELEMENT_MODIFIER_ID = 'whiteboard-element-modifier'

const MOD_KEY = device.mac ? '⌘' : 'Ctrl+'
const copyRow: OzMenuRowsRow = {
  icon: 'copy_outline',
  text: __('Copy'),
  eventPayload: ActionType.Copy,
  rightLabel: `${MOD_KEY}C`,
}
const cutRow: OzMenuRowsRow = {
  icon: 'scissors',
  text: __('Cut'),
  eventPayload: ActionType.Cut,
  rightLabel: `${MOD_KEY}X`,
}
const pasteRow: OzMenuRowsRow = {
  icon: 'clipboard_outline',
  text: __('Paste'),
  eventPayload: ActionType.Paste,
  rightLabel: `${MOD_KEY}V`,
}
const duplicateRow: OzMenuRowsRow = {
  icon: 'duplicate_outline',
  text: __('Duplicate'),
  eventPayload: ActionType.Duplicate,
  rightLabel: `${MOD_KEY}D`,
}
const selectAllRow: OzMenuRowsRow = {
  icon: 'select_all',
  text: __('Select all'),
  eventPayload: ActionType.SelectAll,
  rightLabel: '⇧A',
}
const freezeRow: OzMenuRowsRow = {
  icon: 'freeze',
  text: __('Freeze'),
  eventPayload: ActionType.ToggleFreeze,
  rightLabel: '⇧F',
}
const unfreezeRow: OzMenuRowsRow = {
  icon: 'freeze',
  text: __('Unfreeze'),
  eventPayload: ActionType.ToggleFreeze,
}
const sendToFrontRow: OzMenuRowsRow = {
  icon: 'send_to_front_outline',
  text: __('Send to front'),
  eventPayload: ActionType.SendToFront,
  rightLabel: ']',
}
const sendToBackRow: OzMenuRowsRow = {
  icon: 'send_to_back_outline',
  text: __('Send to back'),
  eventPayload: ActionType.SendToBack,
  rightLabel: '[',
}
const groupRow: OzMenuRowsRow = {
  icon: 'group_shapes',
  text: __('Group'),
  eventPayload: ActionType.Group,
  rightLabel: `${MOD_KEY}G`,
}
const ungroupRow: OzMenuRowsRow = {
  icon: 'ungroup_shapes',
  text: __('Ungroup'),
  eventPayload: ActionType.Ungroup,
  rightLabel: `${MOD_KEY}⇧G`,
}
const deleteRow: OzMenuRowsRow = {
  icon: 'delete',
  text: __('Delete'),
  eventPayload: ActionType.Delete,
  rightLabel: '⌫',
  rowStyle: OzMenuRowsRowStyle.Danger,
}
const editPollRow: OzMenuRowsRow = {
  icon: 'pencil_outline',
  text: __('Edit poll'),
  eventPayload: ActionType.EditPoll,
}
const openPollRow: OzMenuRowsRow = {
  icon: 'poll_open',
  text: __('Open poll'),
  eventPayload: ActionType.OpenPoll,
}
const closePollRow: OzMenuRowsRow = {
  icon: 'poll_close',
  text: __('Close poll'),
  eventPayload: ActionType.ClosePoll,
}
const exportPollRow: OzMenuRowsRow = {
  icon: 'download_outline',
  text: __('Export poll results as CSV'),
  eventPayload: ActionType.ExportPoll,
}
const sharingAndPermissionsRow: OzMenuRowsRow = {
  icon: 'share_arrow_outline',
  text: __('Sharing & permissions'),
  eventPayload: ActionType.ShareAndPermissions,
}
const changeBackgroundRow: OzMenuRowsRow = {
  icon: 'picture_frame',
  text: __('Change background'),
  eventPayload: ActionType.ChangeBackground,
}
const setAsCardBackgroundRow: OzMenuRowsRow = {
  icon: 'picture_frame',
  text: __('Set as card background'),
  eventPayload: ActionType.SetAsCardBackground,
}

function mapTldrawColorToWhiteboardColor(
  secondaryMenuItems: WhiteboardTool[],
  tldrawColor: TldrawDefaultColorStyleColor,
): ToolTailwindColorType {
  return secondaryMenuItems.find((item) => item.tldrawToolId === tldrawColor)?.id as ToolTailwindColorType
}

type FillStyle = 'none' | 'semi' | 'solid'

function getFillStyleIconName(fillStyle: FillStyle): string {
  switch (fillStyle) {
    case 'none':
      return 'fill-none-element-modifier'
    case 'semi':
      return 'fill-white-element-modifier'
    case 'solid':
      return 'fill-solid-element-modifier'
    default:
      return ''
  }
}

function getTextSizeLabel(textSize: WhiteboardTextSize | null): string {
  switch (textSize) {
    case 'auto':
      // whiteboard-element-modifier-text-size-label is defined in WhiteboardElementModifier.vue
      return '<p class="whiteboard-element-modifier-text-size-label"><span>A</span><span>A</span></p>'
    case 's':
    case 's1':
      return __('Small')
    case 'm':
    case 'm1':
    case 'xl':
      // Handle legacy shapes with 'xl' size
      return __('Medium')
    case 'l':
    case 'l1':
      return __('Large')
    default:
      return ''
  }
}

const THEME_FONT_MENU_ITEM_ID = 'themeFont'

function getFontCssClasses(fontId: FontNameToFontId): string {
  return tailwindWhiteboardFontClass(fontId)
}

type ConnectorStyle = [TLArrowShapeArrowheadStyle, TLDefaultDashStyle, TLArrowShapeArrowheadStyle]

function getConnectorStyleIconName(connectorStyle: ConnectorStyle): string {
  const [arrowheadStart, dash, arrowheadEnd] = connectorStyle

  // Connector styles are a combination of three arrow shape props
  // We offer menu items to toggle them in tandem instead of individually
  const connectorStyleStringified = `${arrowheadStart}-${dash}-${arrowheadEnd}`
  switch (connectorStyleStringified) {
    case 'arrow-draw-arrow':
      return 'solid_double'
    case 'none-draw-arrow':
      return 'solid_single'
    case 'none-draw-none':
      return 'solid_none'
    case 'arrow-dashed-arrow':
      return 'dash_double'
    case 'none-dashed-arrow':
      return 'dash_single'
    case 'none-dashed-none':
      return 'dash_none'
    default:
      return ''
  }
}

function isShapeWithRichText(shape: TLShape | TLRecord): boolean {
  if (shape == null) return false
  const shapes = isGroupShape(shape as TLShape) ? getAllShapesInGroup(shape as TLShape) : [shape]

  return shapes.every((currentShape) => currentShape.typeName === 'shape' && 'richText' in currentShape.props)
}

function getTextShapes(shapes: TLShape[]): TLShape[] {
  return shapes.filter((shape) => isShapeWithRichText(shape))
}

function updateRichTextOfShapes(
  shapes: TLShape[],
  operation: (payload: { shape: TLShape; content: Content }) => Content,
): void {
  const editor = useTldrawEditor().editor
  if (editor == null || shapes.length === 0) return

  editor.run(() => {
    const textShapes = getTextShapes(shapes)
    if (textShapes.length === 0) return

    const richTextMap = new Map<TLShapeId, Content>()
    textShapes.forEach((shape) => {
      if (!('richText' in shape.props)) return
      richTextMap.set(shape.id, operation({ shape, content: shape.props.richText as Content }))
    })

    const shapesToUpdate = shapes
      .filter((shape) => richTextMap.has(shape.id))
      .map((shape) => ({
        id: shape.id,
        type: shape.type,
        props: { richText: richTextMap.get(shape.id) },
      }))

    editor.updateShapes(shapesToUpdate)

    // HACK: In `pinia/whiteboard_element_modifier.ts`, there are a lot of computed properties that depend on the selected shapes
    // AND each shape's `richText` content. For example, you can take a look at `nonEmptyTextShapes`:
    //
    // const nonEmptyTextShapes = computed(() => {
    //   return allSelectedShapes.value.filter((shape) => {
    //     if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
    //     return !useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isEmpty
    //   })
    // })
    //
    // However, `shape.props.richText` is not reactive. We never have deep reactivity for Tldraw shapes (even if we try,
    // it'll break as Tldraw has its own reactivity system).
    // So in order to recompute those computed properties, we need to trigger `allSelectedShapes` to recompute, and the
    // way to do that is to unselect then reselect the shapes, which will cause the Tldraw reactive effect in `whiteboard_element_modifier`
    // to run again.
    //
    // useTldrawEditor().react((editor) => {
    //   selectedShapes.value = editor.getSelectedShapes()
    // })
    //
    // So here's our hack:
    const originalSelection = editor.getSelectedShapes()
    editor.selectNone()
    editor.select(...originalSelection)
  })
}

function setTextShapesBold(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).setBold())
}

function unsetTextShapesBold(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).unsetBold())
}

function setTextShapesItalic(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).setItalic())
}

function unsetTextShapesItalic(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).unsetItalic())
}

function setTextShapesStrike(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).setStrike())
}

function unsetTextShapesStrike(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).unsetStrike())
}

function unsetTextShapesColor(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).unsetColor())
}

function toggleTextShapesBulletList(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).toggleBulletList())
}

function toggleTextShapesOrderedList(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).toggleOrderedList())
}

function alignTextShapesLeft(shapes: TLShape[]): void {
  const editor = useTldrawEditor().editor
  if (editor == null) return

  const { updateShapesTextAlignment } = useWhiteboardShapeActions()
  const didUpdate = updateShapesTextAlignment(shapes, 'start')

  if (didUpdate) {
    editor.markHistoryStoppingPoint('text align left')
  }
}

function alignTextShapesCenter(shapes: TLShape[]): void {
  const editor = useTldrawEditor().editor
  if (editor == null) return

  const { updateShapesTextAlignment } = useWhiteboardShapeActions()
  const didUpdate = updateShapesTextAlignment(shapes, 'middle')

  if (didUpdate) {
    editor.markHistoryStoppingPoint('text align center')
  }
}

function alignTextShapesRight(shapes: TLShape[]): void {
  const editor = useTldrawEditor().editor
  if (editor == null) return

  const { updateShapesTextAlignment } = useWhiteboardShapeActions()
  const didUpdate = updateShapesTextAlignment(shapes, 'end')

  if (didUpdate) {
    editor.markHistoryStoppingPoint('text align right')
  }
}

enum TextFormattingOption {
  Bold = 'bold',
  Italic = 'italic',
  Strikethrough = 'strikethrough',
  TextAlignment = 'textAlignment',
  BulletedList = 'bulletedList',
  NumberedList = 'numberedList',
}

const textFormattingOptions: Record<TextFormattingOption, WhiteboardTool> = {
  [TextFormattingOption.Bold]: {
    id: TextFormattingOption.Bold,
    iconName: 'bold',
    label: __('Bold'),
    tldrawToolId: '',
  },
  [TextFormattingOption.Italic]: {
    id: TextFormattingOption.Italic,
    iconName: 'italic',
    label: __('Italic'),
    tldrawToolId: '',
  },
  [TextFormattingOption.Strikethrough]: {
    id: TextFormattingOption.Strikethrough,
    iconName: 'strikethrough',
    label: __('Strikethrough'),
    tldrawToolId: '',
  },
  [TextFormattingOption.TextAlignment]: {
    id: TextFormattingOption.TextAlignment,
    iconName: 'align_left', // Just a default icon name but this dynamically changes based on current alignment
    label: __('Text alignment'),
    tldrawToolId: '',
  },
  [TextFormattingOption.BulletedList]: {
    id: TextFormattingOption.BulletedList,
    iconName: 'bulleted_list',
    label: __('Bulleted list'),
    tldrawToolId: '',
  },
  [TextFormattingOption.NumberedList]: {
    id: TextFormattingOption.NumberedList,
    iconName: 'numbered_list',
    label: __('Numbered list'),
    tldrawToolId: '',
  },
}

function getFirstCharacterTextColorClass(shape: TLShape): ToolTailwindColorType | undefined {
  if (shape.type !== 'text' || !('richText' in shape.props)) return
  const richText = shape.props.richText as Content
  const { whiteboardTiptapEditor } = useWhiteboardHeadlessTiptapEditor(richText)

  // Find and select first non-empty character
  const { state } = whiteboardTiptapEditor
  const docSize = state.doc.content.size
  let position = 0

  // Iterate through document to find first non-empty character
  while (position < docSize) {
    const text = state.doc.textBetween(position, position + 1)
    if (text.trim().length > 0) {
      // Found non-empty character, set selection to it
      whiteboardTiptapEditor.commands.setTextSelection({
        from: position,
        to: position + 1,
      })
      break
    }
    position++
  }

  const rgbColor = whiteboardTiptapEditor.getAttributes('textStyle').color
  if (rgbColor == null) return
  const textColorClass = Object.entries(TEXT_COLORS_TO_RGB_COLORS).find(([_, value]) => value === rgbColor)
  if (textColorClass == null) return
  return textColorClass[0] as ToolTailwindColorType
}

/**
 * Scales the currently selected shapes by the given factor.
 * @param scaleFactor - The factor to scale by (e.g., 1.1 to scale up, 1/1.1 to scale down)
 */
function scaleSelectedShapes(scaleFactor: number): void {
  const editor = useTldrawEditor().editor
  if (editor == null) return

  editor.markHistoryStoppingPoint('resize shapes')

  const selectedShapes = editor.getSelectedShapes()

  const scaleOrigin = editor.getSelectionPageBounds()?.center

  editor.run(() => {
    selectedShapes.forEach((shape) => {
      // Using VecModel ({x, y}) instead of Vec since we only need the scale values
      // and don't need the full Vec class functionality. TLDraw's resizeShape accepts
      // either Vec or VecModel through the VecLike type.
      // github.com/padlet/mozart/pull/37924#discussion_r2131601984
      editor.resizeShape(shape.id, { x: scaleFactor, y: scaleFactor }, { scaleOrigin })
    })
  })
}

export {
  alignTextShapesCenter,
  alignTextShapesLeft,
  alignTextShapesRight,
  changeBackgroundRow,
  closePollRow,
  copyRow,
  cutRow,
  deleteRow,
  duplicateRow,
  editPollRow,
  exportPollRow,
  freezeRow,
  getConnectorStyleIconName,
  getFillStyleIconName,
  getFirstCharacterTextColorClass,
  getFontCssClasses,
  getTextSizeLabel,
  groupRow,
  isShapeWithRichText,
  mapTldrawColorToWhiteboardColor,
  MOD_KEY,
  openPollRow,
  pasteRow,
  scaleSelectedShapes,
  selectAllRow,
  sendToBackRow,
  sendToFrontRow,
  setAsCardBackgroundRow,
  setTextShapesBold,
  setTextShapesItalic,
  setTextShapesStrike,
  sharingAndPermissionsRow,
  TextFormattingOption,
  textFormattingOptions,
  THEME_FONT_MENU_ITEM_ID,
  toggleTextShapesBulletList,
  toggleTextShapesOrderedList,
  unfreezeRow,
  ungroupRow,
  unsetTextShapesBold,
  unsetTextShapesColor,
  unsetTextShapesItalic,
  unsetTextShapesStrike,
  WHITEBOARD_ELEMENT_MODIFIER_ID,
}
export type { ConnectorStyle, FillStyle }
